"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import {
  fieldVariantClasses,
  type FieldVariant,
} from "./field-variants";

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "className"> {
  label?: string;
  error?: string;
  helperText?: string;
  maxLength?: number;
  showCount?: boolean;
  variant?: FieldVariant;
  size?: "default" | "sm" | "lg";
  className?: string;
  /** Contenido en la esquina superior derecha del campo (p. ej. botón IA). */
  topEnd?: React.ReactNode;
  /** Muestra el contador dentro del campo, abajo a la derecha. */
  insetCount?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      maxLength,
      showCount = false,
      variant = "subtle",
      size = "sm",
      id,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      className,
      topEnd,
      insetCount = false,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const textareaId = id ?? generatedId;
    const isSm = size === "sm";
    const colors = fieldVariantClasses(variant, { error: Boolean(error) });

    const [isFocused, setIsFocused] = React.useState(false);
    const [charCount, setCharCount] = React.useState(
      String(value !== undefined ? value : (defaultValue ?? "")).length
    );
    const [hasValue, setHasValue] = React.useState(
      Boolean(value !== undefined ? value : defaultValue)
    );

    React.useEffect(() => {
      if (value !== undefined) {
        setHasValue(Boolean(value));
        setCharCount(String(value).length);
      }
    }, [value]);

    const isFloating = isFocused || hasValue;
    const showInsetCount = insetCount && (showCount || maxLength != null);
    const hasTopEnd = Boolean(topEnd);
    /** Mismo padding superior con o sin foco para que no “salte” la altura al flotar el label. */
    const topPad = label
      ? isSm
        ? "pt-[1.25rem]"
        : "pt-[1.8125rem]"
      : isSm
        ? "pt-2"
        : "pt-3";

    return (
      <div className="w-full">
        <div className="relative w-full">
          <textarea
            id={textareaId}
            ref={ref}
            value={value}
            defaultValue={defaultValue}
            maxLength={maxLength}
            className={cn(
              "peer w-full resize-y rounded-2xl border bg-white/70 leading-5 text-foreground shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-200 outline-none dark:bg-zinc-950/70 dark:shadow-[0_1px_2px_rgba(0,0,0,0.35)]",
              "block align-top",
              isSm
                ? "min-h-16 px-3 text-xs leading-snug"
                : "min-h-[120px] px-4 text-sm leading-normal",
              showInsetCount
                ? isSm
                  ? "pb-7"
                  : "pb-8"
                : isSm
                  ? "pb-2"
                  : "pb-3",
              hasTopEnd && (isSm ? "pr-10" : "pr-12"),
              topPad,
              "placeholder:text-transparent",
              "focus:ring-1 focus:ring-offset-0",
              "disabled:cursor-not-allowed disabled:opacity-50",
              colors.border,
              colors.focusBorder,
              colors.focusRing,
              className,
            )}
            placeholder={label ? " " : props.placeholder}
            onFocus={(e) => {
              setIsFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              onBlur?.(e);
            }}
            onChange={(e) => {
              setHasValue(Boolean(e.target.value));
              setCharCount(e.target.value.length);
              onChange?.(e);
            }}
            {...props}
          />

          {label && (
            <label
              htmlFor={textareaId}
              className={cn(
                "pointer-events-none absolute z-20 select-none font-medium transition-all duration-200",
                isSm ? "left-3" : "left-4",
                isFloating
                  ? cn(
                      "top-0 -translate-y-1/2 rounded-2xl bg-white/90 px-1.5 py-0.5 leading-none dark:bg-zinc-950/90",
                      isSm ? "text-[10px]" : "text-xs",
                      colors.label
                    )
                  : cn(
                      "-translate-y-1/2 cursor-text text-muted-foreground",
                      isSm
                        ? "top-[1.25rem] text-xs"
                        : "top-[1.8125rem] text-sm"
                    )
              )}
            >
              {label}
            </label>
          )}

          {hasTopEnd && (
            <div
              className={cn(
                "absolute z-30",
                isSm ? "right-1.5 top-1.5" : "right-2.5 top-2.5"
              )}
            >
              {topEnd}
            </div>
          )}

          {showInsetCount && (
            <p
              className={cn(
                "pointer-events-none absolute z-20 select-none font-medium tabular-nums text-slate-400/70 dark:text-zinc-500/70",
                isSm
                  ? "bottom-2 right-3 text-[10px]"
                  : "bottom-2.5 right-3.5 text-[11px]"
              )}
              aria-live="polite"
            >
              {charCount}
              {maxLength != null && `/${maxLength}`}
            </p>
          )}
        </div>

        <div className="mt-1.5 flex items-center justify-between px-1">
          {(error ?? helperText) ? (
            <p
              className={cn(
                "text-xs",
                error ? "text-destructive" : "text-muted-foreground"
              )}
            >
              {error ?? helperText}
            </p>
          ) : (
            <span />
          )}
          {(showCount || maxLength) && !showInsetCount && (
            <p className="text-xs text-muted-foreground">
              {charCount}
              {maxLength != null && `/${maxLength}`}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export { Textarea };
